import { useMemo } from "react";
import { SectionMeta, SelectItem } from "../types/selectFieldInternalTypes";

export type SectionItem = { item: SelectItem; index: number };

export type PinnedSection = {
  type: "pinned";
  label: string;
  items: SectionItem[];
  loading?: boolean;
};

export type GroupSection = {
  type: "group";
  label: string;
  items: SectionItem[];
};

export type FlatSection = {
  type: "flat";
  items: SectionItem[];
};

export type Section = PinnedSection | GroupSection | FlatSection;

export type VirtualRow =
  | { type: "item"; item: SelectItem; index: number }
  | { type: "header"; label: string; sectionIndex: number }
  | { type: "divider"; sectionIndex: number }
  | { type: "load-more" };

/**
 * Computes grouped sections from a flat items array and section metadata.
 * @param items - The flat items array
 * @param sectionsMeta - Section metadata describing pinned/group ranges
 * @returns Grouped sections for rendering
 */
export const useOptionsSections = (
  items: SelectItem[],
  sectionsMeta: SectionMeta[],
): Section[] => {
  return useMemo((): Section[] => {
    if (sectionsMeta.length === 0) {
      return [
        {
          type: "flat",
          items: items.map((item, index) => ({ item, index })),
        },
      ];
    }

    const result: Section[] = [];

    const firstSectionStartIndex = Math.min(
      ...sectionsMeta.map((s) => s.startIndex),
    );

    if (firstSectionStartIndex > 0) {
      const flatItems: SectionItem[] = [];
      for (let i = 0; i < firstSectionStartIndex; i++) {
        if (items[i]) {
          flatItems.push({ item: items[i], index: i });
        }
      }
      if (flatItems.length > 0) {
        result.push({ type: "flat", items: flatItems });
      }
    }

    sectionsMeta.forEach((meta) => {
      const sectionItems: SectionItem[] = [];
      for (let i = meta.startIndex; i < meta.endIndex; i++) {
        if (items[i]) {
          sectionItems.push({ item: items[i], index: i });
        }
      }
      if (meta.type === "pinned") {
        if (sectionItems.length > 0 || meta.loading) {
          result.push({
            type: "pinned",
            label: meta.label,
            items: sectionItems,
            loading: meta.loading,
          });
        }
      } else if (sectionItems.length > 0) {
        result.push({
          type: "group",
          label: meta.label,
          items: sectionItems,
        });
      }
    });

    const lastSectionIndex =
      sectionsMeta.length > 0
        ? Math.max(...sectionsMeta.map((s) => s.endIndex))
        : 0;

    if (lastSectionIndex < items.length) {
      const flatItems: SectionItem[] = [];
      for (let i = lastSectionIndex; i < items.length; i++) {
        flatItems.push({ item: items[i], index: i });
      }
      if (flatItems.length > 0) {
        result.push({ type: "flat", items: flatItems });
      }
    }

    return result;
  }, [items, sectionsMeta]);
};

/**
 * Flattens grouped sections into a linear list of virtual rows for virtualization,
 * and builds a mapping from downshift item index to virtual row index.
 * @param sections - The grouped sections from useOptionsSections
 * @param hasMore - Whether there are more items to load
 * @returns The flat virtual row list and a downshift-to-virtual index mapping
 */
export function flattenSectionsToVirtualRows(
  sections: Section[],
  hasMore: boolean,
): {
  virtualRows: VirtualRow[];
  downshiftIndexToVirtualRowIndex: Record<number, number>;
  stickyIndices: number[];
} {
  const virtualRows: VirtualRow[] = [];
  const downshiftIndexToVirtualRowIndex: Record<number, number> = {};
  const stickyIndices: number[] = [];

  sections.forEach((section, sectionIndex) => {
    const isLastSection = sectionIndex === sections.length - 1;

    if (section.type === "pinned" || section.type === "group") {
      virtualRows.push({
        type: "header",
        label: section.label,
        sectionIndex,
      });

      section.items.forEach(({ item, index }) => {
        downshiftIndexToVirtualRowIndex[index] = virtualRows.length;
        virtualRows.push({ type: "item", item, index });
      });

      if (!isLastSection) {
        virtualRows.push({ type: "divider", sectionIndex });
      }
    } else {
      section.items.forEach(({ item, index }) => {
        const isBulkAction =
          item.type === "select-all" || item.type === "select-filtered";
        if (isBulkAction) {
          stickyIndices.push(virtualRows.length);
        }
        downshiftIndexToVirtualRowIndex[index] = virtualRows.length;
        virtualRows.push({ type: "item", item, index });
      });
    }
  });

  if (hasMore) {
    virtualRows.push({ type: "load-more" });
  }

  return { virtualRows, downshiftIndexToVirtualRowIndex, stickyIndices };
}
